import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { OrderLinkingDialogComponent } from './order-linking-dialog.component';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { toUtcIsoDate } from '../utils/date-utils';

@Component({
  standalone: true,
  selector: 'app-purchaseorder',
  templateUrl: './purchaseorder.component.html',
  styleUrl: './purchaseorder.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatTableModule,
    MatDialogModule
  ]
})
export class PurchaseOrderComponent implements OnInit {
  purchaseOrderForm: FormGroup;
  unitTypes: string[] = [];
  orderStatuses: any[] = [];
  vendorContacts: any[] = [];
  vendorAddresses: any[] = [];
  vendors: any[] = [];
  productsList: any[] = [];
  internalShippingAddresses: any[] = [];
  filteredVendors!: Observable<any[]>;
  filteredProductsList: Observable<any[]>[] = [];
  purchaseOrderId: number | null = null;
  isEditMode: boolean = false;
  salesOrders: any[] = [];
  salesOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  salesInvoices: any[] = [];
  salesInvoiceColumns = ['invoiceNumber', 'date', 'status', 'totalAmount', 'siActions'];

  constructor(
    private fb: FormBuilder, 
    private router: Router, 
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.purchaseOrderForm = this.fb.group({
      // Order base class properties
      number: ['', Validators.required],
      date: [new Date(), Validators.required],
      status: ['Draft'],
      shipper: ['FedEx Priority Overnight'],
      shipAccountNum: [''],
      shipTerms: ['FOB Vendor'],
      desiredShipDate: [null],
      shipAttentionTo: ['Randy'],
      salesPerson: ['Patrick'],
      taxAmount: [0, [Validators.min(0)]],
      shipFee: [0, [Validators.min(0)]],
      handlingFee: [0, [Validators.min(0)]],
      discountAmount: [0, [Validators.min(0)]],
      notes: [''],
      
      // PurchaseOrder specific properties
      qboId: [null],
      vendorId: [null, Validators.required],
      vendorSearch: [''],
      vendorAddressId: [null],
      vendorContactId: [null],
      shippingAddressId: [2179],
      billingAddressId: [2181],
      
      // Products array
      products: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadUnitTypes();
    this.loadOrderStatuses();
    this.loadVendors();
    this.loadProducts();
    this.loadInternalShippingAddresses();
    
    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.purchaseOrderId = +id;
        this.isEditMode = false;
        this.loadPurchaseOrder(this.purchaseOrderId);
        this.loadRelatedSalesOrders(this.purchaseOrderId);
      } else {
        this.isEditMode = true;

        // Pre-populate a new purchase order from another one (e.g. "Copy" action)
        const prefill = history.state?.prefill;
        if (prefill) {
          this.populatePurchaseOrderForm(prefill, true);
        }
        this.loadLatestOrderNumber();
        this.loadShippingAccount();
      }
    });
    
    // Setup vendor search autocomplete
    this.filteredVendors = this.purchaseOrderForm.get('vendorSearch')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterVendors(value || ''))
    );
    
    // Watch for vendorId changes to load related contacts and addresses
    this.purchaseOrderForm.get('vendorId')?.valueChanges.subscribe(vendorId => {
      if (vendorId) {
        // Clear previously selected contact and address values
        this.purchaseOrderForm.patchValue({
          vendorContactId: null,
          vendorAddressId: null
        }, { emitEvent: false });
        
        // Load new contacts and addresses for the selected vendor
        this.loadVendorContacts(vendorId);
        this.loadVendorAddresses(vendorId);
      } else {
        this.vendorContacts = [];
        this.vendorAddresses = [];
        this.purchaseOrderForm.patchValue({
          vendorContactId: null,
          vendorAddressId: null
        }, { emitEvent: false });
      }
    });
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.setSelectsDisabled(false);
    
    const formValue = this.purchaseOrderForm.value;
    if (formValue.vendorId > 0) {
      this.loadVendorContacts(formValue.vendorId);
      this.loadVendorAddresses(formValue.vendorId);
    }
  }

  private setSelectsDisabled(disabled: boolean): void {
    const controls = ['status', 'vendorAddressId', 'vendorContactId', 'shippingAddressId', 'billingAddressId'];
    controls.forEach(ctrl => {
      const control = this.purchaseOrderForm.get(ctrl);
      disabled ? control?.disable({ emitEvent: false }) : control?.enable({ emitEvent: false });
    });
    this.products.controls.forEach(product => {
      const unitType = product.get('unitType');
      disabled ? unitType?.disable({ emitEvent: false }) : unitType?.enable({ emitEvent: false });
    });
  }

  loadPurchaseOrder(id: number): void {
    while (this.products.length) this.products.removeAt(0);
    this.apiService.get<any>(`purchaseorder/${id}`).subscribe({
      next: (data) => {
        this.populatePurchaseOrderForm(data, false);
      },
      error: (error) => {
        console.error('Error loading purchase order:', error);
      }
    });
  }
    
    loadShippingAccount(): void {
    this.apiService.get<any>(`purchaseorder/internal-shipping-account`).subscribe({
      next: (data) => {
        this.purchaseOrderForm.patchValue({
          shipAccountNum:  data.shippingAccount || ''
        });
      },
      error: (error) => {
        console.error('Error loading po shipping account:', error);
      }
    });
  }

  // Populates the purchase order form and products array from API data.
  // When isCopy is true, the order number/status/QBO id are reset so the
  // result is saved as a brand-new purchase order rather than an update.
  private populatePurchaseOrderForm(data: any, isCopy: boolean): void {
    // Handle both camelCase and PascalCase from API
    const purchaseOrder = {
      number: isCopy ? '' : (data.number || data.Number || ''),
      date: isCopy ? new Date() : ((data.date || data.Date) ? new Date(data.date || data.Date) : new Date()),
      status: isCopy ? 'Draft' : (data.status || data.Status || 'Draft'),
      shipper: data.shipper || data.Shipper || '',
      shipAccountNum: data.shipAccountNum || data.ShipAccountNum || '',
      shipTerms: data.shipTerms || data.ShipTerms || '',
      desiredShipDate: isCopy ? null : ((data.desiredShipDate || data.DesiredShipDate) ? new Date(data.desiredShipDate || data.DesiredShipDate) : null),
      shipAttentionTo: data.shipAttentionTo || data.ShipAttentionTo || '',
      salesPerson: data.salesPerson || data.SalesPerson || '',
      taxAmount: data.taxAmount || data.TaxAmount || 0,
      shipFee: data.shippingAmount || data.ShippingAmount || 0,
      handlingFee: data.handlingFee || data.HandlingFee || 0,
      discountAmount: data.discountAmount || data.DiscountAmount || 0,
      notes: data.externalNotes || data.ExternalNotes || '',
      qboId: isCopy ? null : (data.qboId || data.QboId || data.QBO_Id || null),
      vendorId: data.vendorId || data.VendorId || null,
      vendorAddressId: data.vendorAddressId || data.VendorAddressId || null,
      vendorContactId: data.vendorContactId || data.VendorContactId || null,
      shippingAddressId: data.shippingAddressId || data.ShippingAddressId || null,
      billingAddressId: data.billingAddressId || data.BillingAddressId || null
    };
    
    // Check if API response includes nested vendor contact and address objects
    const vendorContact = data.vendorContact || data.VendorContact;
    const vendorAddress = data.vendorAddress || data.VendorAddress;
    const vendorObj = data.vendor || data.Vendor;
    
    // If nested objects exist, populate the arrays directly
    if (vendorContact) {
      this.vendorContacts = [vendorContact];
    }
    if (vendorAddress) {
      this.vendorAddresses = [vendorAddress];
    }
    
    // Patch form values without triggering valueChanges for vendorId
    this.purchaseOrderForm.patchValue(purchaseOrder, { emitEvent: false });

    // Set the vendor for the search field display
    if (purchaseOrder.vendorId) {
      // First try to use nested vendor object from API
      if (vendorObj) {
        this.purchaseOrderForm.patchValue({ vendorSearch: vendorObj }, { emitEvent: false });
      } else {
        // Otherwise, try to find from loaded vendors array or fetch it
        const vendor = this.vendors.find(v => (v.id || v.Id) === purchaseOrder.vendorId);
        if (vendor) {
          this.purchaseOrderForm.patchValue({ vendorSearch: vendor }, { emitEvent: false });
        } else {
          // Load the specific vendor if not found in the list
          this.apiService.get<any>(`vendor/${purchaseOrder.vendorId}`).subscribe({
            next: (vendorData) => {
              this.purchaseOrderForm.patchValue({ vendorSearch: vendorData }, { emitEvent: false });
            },
            error: (err) => console.error('Error loading vendor:', err)
          });
        }
      }
    }
    
    // If nested data wasn't provided, trigger the load methods manually
    if (purchaseOrder.vendorId && (!vendorContact || !vendorAddress)) {
      if (!vendorContact) {
        this.loadVendorContacts(purchaseOrder.vendorId);
      }
      if (!vendorAddress) {
        this.loadVendorAddresses(purchaseOrder.vendorId);
      }
    }
    
    // Load products if available
    const productsData = data.products || data.Products || [];
    productsData.forEach((product: any) => {
      const vendorProduct = product.vendorProduct || product.VendorProduct;
      const lotNumNav = product.lotNum || product.LotNum;
      this.products.push(this.fb.group({
        id: [isCopy ? null : (product.id || product.Id || null)],
        productId: [product.productId || product.ProductId || ''],
        productSearch: [''],
        catalogNum: [product.catalogNum || product.CatalogNum || ''],
        lotNumId: [product.lotNumId || product.LotNumId || null],
        lotNum: [product.lotNum || product.LotNum || ''],
        fitzidxLotNumber: [lotNumNav?.number || lotNumNav?.Number || ''],
        description: [product.product?.description || product.product?.Description || ''],
        partNum: [product.partNum || product.PartNum || ''],
        vendorCatalogNum: [vendorProduct ? (vendorProduct.catalogNum || vendorProduct.CatalogNum || '') : ''],
        vendorLotNum: [product.vendorLotNum || product.VendorLotNum || ''],
        desiredPackaging: [product.desiredPackaging || product.DesiredPackaging || ''],
        vendorProductId: [vendorProduct ? (vendorProduct.id || vendorProduct.Id || null) : null],
        unitPrice: [product.unitPrice || product.UnitPrice || 0],
        unitType: [product.unitType || product.UnitType || ''],
        quantity: [product.quantity || product.Quantity || 1],
        lineNumber: [product.lineNumber || product.LineNumber || 0],
        notes: [product.notes || product.Notes || '']
      }));
      this.setupProductAutocomplete(this.products.length - 1);
    });
    this.enrichLineItems();
    this.setSelectsDisabled(!isCopy);
  }

  loadUnitTypes(): void {
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => {
        this.unitTypes = data;
      },
      error: (err) => {
        console.error('Error loading unit types:', err);
        // Fallback to default values
        this.unitTypes = [];
      }
    });
  }

  loadOrderStatuses(): void {
    this.apiService.get<string[]>('misc/orderStatuses').subscribe({
      next: (data) => {
        this.orderStatuses = data;
      },
      error: (err) => {
        console.error('Error loading order statuses:', err);
        this.orderStatuses = [];
      }
    });
  }

   loadLatestOrderNumber(): void {
    this.apiService.get<any>(`purchaseorder/latest-number`).subscribe({
      next: (data) => {
        this.purchaseOrderForm.patchValue({ number: data.orderNumber }, { emitEvent: false });
      },
      error: (err) => {
        console.error('Error loading latest order number:', err);
        this.purchaseOrderForm.patchValue({ number: '' }, { emitEvent: false });
      }
    });
  }

  loadVendors(): void {
    this.apiService.get<any[]>('vendor/all').subscribe({
      next: (data) => {
        this.vendors = data || [];
      },
      error: (err) => {
        console.error('Error loading vendors:', err);
        this.vendors = [];
      }
    });
  }

  loadInternalShippingAddresses(): void {
    this.apiService.get<any[]>('misc/internalAddresses/active').subscribe({
      next: (data) => {
        this.internalShippingAddresses = data || [];
      },
      error: (err) => {
        console.error('Error loading internal addresses:', err);
        this.internalShippingAddresses = [];
      }
    });
  }

  private _filterVendors(value: string | any): any[] {
    const filterValue = (typeof value === 'string' ? value : (value?.name || value?.Name || '')).toLowerCase();
    return this.vendors.filter(vendor => {
      const name = vendor.name || vendor.Name || '';
      const id = (vendor.id || vendor.Id || '').toString();
      return name.toLowerCase().includes(filterValue) || id.includes(filterValue);
    });
  }

  displayVendor(vendor: any): string {
    return vendor ? `${vendor.name || vendor.Name || ''} (ID: ${vendor.id || vendor.Id || ''})` : '';
  }

  onVendorSelected(vendor: any): void {
    if (vendor) {
      const vendorId = vendor.id || vendor.Id;
      this.purchaseOrderForm.patchValue({ vendorId: vendorId });
    }
  }

  clearVendor(): void {
    this.purchaseOrderForm.patchValue({
      vendorSearch: '',
      vendorId: null,
      vendorContactId: null,
      vendorAddressId: null
    });
    this.vendorContacts = [];
    this.vendorAddresses = [];
  }

  loadVendorContacts(vendorId: string | number): void {
    this.apiService.get<any[]>(`contact/vendor/all/${vendorId}`).subscribe({
      next: (data) => {
        this.vendorContacts = data || [];
        // Manually trigger change detection to update the mat-select dropdown
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading vendor contacts:', err);
        this.vendorContacts = [];
      }
    });
  }

  loadVendorAddresses(vendorId: string | number): void {
    this.apiService.get<any[]>(`address/vendor/all/${vendorId}`).subscribe({
      next: (data) => {
        this.vendorAddresses = data || [];
        // Manually trigger change detection to update the mat-select dropdowns
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading vendor addresses:', err);
        this.vendorAddresses = [];
      }
    });
  }

  loadProducts(): void {
    this.apiService.get<any[]>('product/allWithLotNums').subscribe({
      next: (data) => {
        this.productsList = data || [];
        this.enrichLineItems();
      },
      error: (err) => {
        console.error('Error loading products:', err);
        this.productsList = [];
      }
    });
  }

  private enrichLineItems(): void {
    if (!this.productsList.length) return;
    this.products.controls.forEach((control) => {
      const productId = control.get('productId')?.value;
      if (productId) {
        const found = this.productsList.find(p => (p.id || p.Id) == productId);
        if (found) {
          control.patchValue({
            productSearch: { type: 'product', product: found, displayLabel: found.name || found.Name || '' },
            catalogNum: found.catalogNum || found.CatalogNum || '',
            lotNum: found.lotNum || found.LotNum || ''
          }, { emitEvent: false });
        }
      }
    });
  }

  setupProductAutocomplete(index: number): void {
    const control = this.products.at(index).get('productSearch')!;
    this.filteredProductsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterProducts(value || ''))
    );
  }

  _filterProducts(value: string | any): any[] {
    if (typeof value !== 'string') return [];
    const filterValue = value.toLowerCase();
    const selectedVendorId = this.purchaseOrderForm.get('vendorId')?.value || null;
    const results: any[] = [];
    this.productsList.forEach(p => {
      const vendorProducts: any[] = p.vendorProducts || p.VendorProducts || [];
      const vendorProductsForVendor = selectedVendorId
        ? vendorProducts.filter((vp: any) => (vp.vendorId || vp.VendorId) == selectedVendorId)
        : vendorProducts;

      const name = (p.name || p.Name || '').toLowerCase();
      const catalog = (p.catalogNum || p.CatalogNum || '').toLowerCase();
      const lot = (p.lotNum || p.LotNum || '').toLowerCase();
      const productTextMatches = name.includes(filterValue) || catalog.includes(filterValue) || lot.includes(filterValue);

      // Always include the product option when text matches; vendor-specific rows are added separately below
      const includeProductOption = productTextMatches;

      if (includeProductOption) {
        results.push({ type: 'product', product: p, displayLabel: p.name || p.Name || '' });
      }

      vendorProductsForVendor.forEach((vp: any) => {
        const vpCatalog = (vp.catalogNum || vp.CatalogNum || '').toLowerCase();
        if (vpCatalog && vpCatalog.includes(filterValue)) {
          results.push({
            type: 'vendorProduct',
            product: p,
            vendorProduct: vp,
            displayLabel: `${p.name || p.Name || ''} [Vendor: ${vp.catalogNum || vp.CatalogNum}]`
          });
        }
      });
    });
    return results;
  }

  displayProduct(option: any): string {
    if (!option) return '';
    if (option.displayLabel) return option.displayLabel;
    return option.name || option.Name || '';
  }

  onProductSelected(index: number, option: any): void {
    const p = option.product || option;
    const productId = p.id || p.Id;
    const catalogNum = p.catalogNum || p.CatalogNum || '';
    const lotNum = p.lotNum || p.LotNum || '';
    const description = p.description || p.Description || '';
    if (option.type === 'vendorProduct' && option.vendorProduct) {
      const vp = option.vendorProduct;
      const vendorCatalogNum = vp.catalogNum || vp.CatalogNum || '';
      const vendorProductId = vp.id || vp.Id || null;
      this.products.at(index).patchValue({ productId, catalogNum, lotNum, description, vendorCatalogNum, vendorProductId });
    } else {
      this.products.at(index).patchValue({ productId, catalogNum, lotNum, description });
    }
  }

  clearProduct(index: number): void {
    this.products.at(index).patchValue({ productSearch: '', productId: '', catalogNum: '', lotNum: '' });
  }

  // Getter for products FormArray
  get products(): FormArray {
    return this.purchaseOrderForm.get('products') as FormArray;
  }

  // Get descriptive title for a product line item
  getProductTitle(index: number): string {
    const product = this.products.at(index).value;
    const productId = product.productId || product.ProductId || '';
    const quantity = product.quantity || product.Quantity || '';
    const unitType = product.unitType || product.UnitType || 'each';
    const unitPrice = product.unitPrice || product.UnitPrice || 0;
    const total = this.calculateLineTotal(index);
    const catalogNum = product.catalogNum || product.CatalogNum || '';
    const search = product.productSearch;
    const productName = search
      ? (typeof search === 'object'
          ? (search.displayLabel || (search.product ? (search.product.name || search.product.Name || '') : '') || search.name || search.Name || '')
          : String(search))
      : (productId ? `Product ${productId}` : '');
    const nameWithCatalog = productName
      ? (catalogNum ? `${productName} [${catalogNum}]` : productName)
      : '';
    if (nameWithCatalog && quantity) {
      return `${nameWithCatalog} - ${quantity} ${unitType} @ $${unitPrice} = ${total}`;
    } else if (nameWithCatalog) {
      return nameWithCatalog;
    }
    return `Product Line ${index + 1}`;
  }

  // Create a new product line item FormGroup
  createProductLineItem(): FormGroup {
    return this.fb.group({
      productId: ['', Validators.required],
      productSearch: [''],
      catalogNum: [''],
      lotNumId: [''],
      lotNum: [''],
      fitzidxLotNumber: [''],
      description: [''],
      partNum: [''],
      vendorCatalogNum: ['', Validators.required],
      vendorLotNum: [''],
      desiredPackaging: [''],
      vendorProductId: [null],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      unitType: ['mg'],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      lineNumber: [this.products.length + 1],
      notes: ['']
    });
  }

  // Add a new product line item
  addProduct(): void {
    this.products.push(this.createProductLineItem());
    this.setupProductAutocomplete(this.products.length - 1);
  }

  // Remove a product line item
  removeProduct(index: number): void {
    this.products.removeAt(index);
    // Update line numbers after removal
    this.updateLineNumbers();
  }

  // Update line numbers for all products
  updateLineNumbers(): void {
    this.products.controls.forEach((control, index) => {
      control.get('lineNumber')?.setValue(index + 1);
    });
  }

  // Calculate line total for a product
  calculateLineTotal(index: number): number {
    const product = this.products.at(index);
    const unitPrice = product.get('unitPrice')?.value || 0;
    const quantity = product.get('quantity')?.value || 0;
    return unitPrice * quantity;
  }

  // Calculate total for line items only (before fees and taxes)
  getLineItemsTotal(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateLineTotal(i);
    }
    return total;
  }

  // Calculate total for all products
  calculateTotal(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateLineTotal(i);
    }
    // Add fees and taxes
    const taxAmount = this.purchaseOrderForm.get('taxAmount')?.value || 0;
    const shipFee = this.purchaseOrderForm.get('shipFee')?.value || 0;
    const handlingFee = this.purchaseOrderForm.get('handlingFee')?.value || 0;
    const discountAmount = this.purchaseOrderForm.get('discountAmount')?.value || 0;
    return total + taxAmount + shipFee + handlingFee - discountAmount;
  }

  onSubmit() {
    if (this.purchaseOrderForm.valid) {
      const formValue = this.purchaseOrderForm.value;
      
      // Prepare data for API (convert to PascalCase for C# backend)
      var purchaseOrderData: any = {
        Number: formValue.number,
        Date: toUtcIsoDate(formValue.date),
        Status: formValue.status,
        Shipper: formValue.shipper,
        ShipAccountNum: formValue.shipAccountNum,
        ShipTerms: formValue.shipTerms,
        DesiredShipDate: toUtcIsoDate(formValue.desiredShipDate),
        ShipAttentionTo: formValue.shipAttentionTo,
        SalesPerson: formValue.salesPerson,
        TaxAmount: formValue.taxAmount,
        ShippingAmount: formValue.shipFee,
        HandlingFee: formValue.handlingFee,
        DiscountAmount: formValue.discountAmount,
        TotalAmount: this.calculateTotal(),
        ExternalNotes: formValue.notes,
        //QBO_Id: formValue.qboId,
        VendorId: formValue.vendorId,
        VendorAddressId: formValue.vendorAddressId,
        VendorContactId: formValue.vendorContactId,
        ShippingAddressId: formValue.shippingAddressId,
        BillingAddressId: formValue.billingAddressId,
        Products: formValue.products.map((p: any) => ({
          ProductId: p.productId,
          UnitPrice: p.unitPrice,
          ...(formValue.status === 'Draft' && !p.unitType ? {} : { UnitType: p.unitType }),
          Quantity: p.quantity,
          LineNumber: p.lineNumber,
          Notes: p.notes,
          VendorLotNum: p.vendorLotNum || '',
          DesiredPackaging: p.desiredPackaging || '',
          PartNum: p.partNum || '',
          VendorProduct: { Id: 0, VendorId: formValue.vendorId, CatalogNum: p.vendorCatalogNum, ProductId: p.productId }
        }))
      };
      
      if (this.purchaseOrderId) {
        purchaseOrderData.Id = this.purchaseOrderId;
        // map products with existing ids
        purchaseOrderData.Products = formValue.products.map((p: any) => ({
          Id: p.id,
          ProductId: p.productId,
          UnitPrice: p.unitPrice,
          ...(formValue.status === 'Draft' && !p.unitType ? {} : { UnitType: p.unitType }),
          Quantity: p.quantity,
          LineNumber: p.lineNumber,
          Notes: p.notes,
          VendorLotNum: p.vendorLotNum || '',
          DesiredPackaging: p.desiredPackaging || '',
          PartNum: p.partNum || '',
          VendorProduct: { Id: p.vendorProductId || 0, VendorId: formValue.vendorId, CatalogNum: p.vendorCatalogNum, ProductId: p.productId }
        }))
        this.apiService.put<any>(`purchaseorder/${this.purchaseOrderId}`, purchaseOrderData).subscribe({
          next: () => this.router.navigate(['/purchase-orders']),
          error: (err) => console.error('Error updating purchase order:', err)
        });
      } else {
        this.apiService.post<any>('purchaseorder/', purchaseOrderData).subscribe({
          next: () => this.router.navigate(['/purchase-orders']),
          error: (err) => console.error('Error creating purchase order:', err)
        });
      }
    }
  }

  onCancel() {
    if (this.purchaseOrderId) {
      this.isEditMode = false;
      this.loadPurchaseOrder(this.purchaseOrderId);
    } else {
      this.router.navigate(['/purchase-orders']);
    }
  }

  openLinkDialog(): void {
    const ref = this.dialog.open(OrderLinkingDialogComponent, {
      data: {
        orderLabel: 'Sales Order',
        fetchUrl: 'salesorder/all',
        linkUrlPrefix: `purchaseorder/${this.purchaseOrderId}/link-salesorder`,
        alreadyLinkedIds: this.salesOrders.map(so => so.id)
      },
      width: '500px'
    });
    ref.afterClosed().subscribe(result => {
      if (result?.linkedOrder) {
        this.loadRelatedSalesOrders(this.purchaseOrderId!);
      }
    });
  }

  loadRelatedSalesOrders(id: number): void {
    this.apiService.get<any[]>(`purchaseorder/${id}/salesorders`).subscribe({
      next: (data) => {
        const rawSalesOrders: any[] = data || [];
        this.salesOrders = rawSalesOrders.map((so: any) => ({
          id: so.id || so.Id,
          number: so.number || so.Number || '',
          date: so.date || so.Date ? new Date(so.date || so.Date) : null,
          status: so.status || so.Status || '',
          totalAmount: so.totalAmount || so.TotalAmount || 0
        }));
        const mapInvoice = (si: any) => ({
          id: si.id || si.Id,
          invoiceNumber: si.invoiceNumber || si.InvoiceNumber || si.number || si.Number || '',
          date: si.invoiceDate || si.InvoiceDate || si.date || si.Date ? new Date(si.invoiceDate || si.InvoiceDate || si.date || si.Date) : null,
          status: si.status || si.Status || '',
          totalAmount: si.totalAmount || si.TotalAmount || 0
        });
        const soInvoices: any[] = rawSalesOrders.flatMap((so: any) =>
          (so.invoices || so.Invoices || so.salesInvoices || so.SalesInvoices || []).map(mapInvoice)
        );
        const invoiceMap = new Map<number, any>();
        soInvoices.forEach(si => invoiceMap.set(si.id, si));
        this.salesInvoices = Array.from(invoiceMap.values());
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related sales orders:', err)
    });
  }

  viewSalesOrder(id: number): void {
    this.router.navigate(['/orders', id]);
  }

  removeSalesOrder(id: number): void {
    this.apiService.delete<any>(`purchaseorder/${this.purchaseOrderId}/unlink-salesorder/${id}`).subscribe({
      next: () => this.loadRelatedSalesOrders(this.purchaseOrderId!),
      error: (err) => console.error('Error unlinking sales order:', err)
    });
  }

  viewSalesInvoice(id: number): void {
    this.router.navigate(['/sales-invoices', id]);
  }

  downloadPdf(): void {
    this.apiService.getBlob(`purchaseorder/${this.purchaseOrderId}/pdf`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.purchaseOrderForm.get('number')?.value || this.purchaseOrderId;
        a.download = `${number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => console.error('Error downloading PDF:', err)
    });
  }

  formatAddress(address: any): string {
    const parts = [
      address.addressLine1 || address.AddressLine1,
      address.addressLine2 || address.AddressLine2,
      address.addressLine3 || address.AddressLine3,
      address.addressLine4 || address.AddressLine4,
      address.addressLine5 || address.AddressLine5,
      address.city || address.City,
      address.state || address.State,
      address.postalCode || address.PostalCode,
      address.country || address.Country
    ].filter(Boolean);
    return parts.join(', ') || '-';
  }

  getAddressTooltip(id: any, addresses: any[]): string {
    if (!id) return '';
    const found = addresses.find(a => (a.id || a.Id) == id);
    return found ? this.formatAddress(found) : '';
  }

  generateSalesOrder(): void {
    const formValue = this.purchaseOrderForm.getRawValue();
    const prefill = {
      poId: this.purchaseOrderId,
      poNumber: formValue.number || '',
      poDate: formValue.date || '',
      poStatus: formValue.status || '',
      poTotal: this.calculateTotal() || 0,
      salesPerson: formValue.salesPerson || '',
      products: formValue.products.map((p: any) => ({
        purchaseOrderProductId: p.id,
        productId: p.productId,
        lotNumId: p.lotNumId,
        vendorLotNum: p.vendorLotNum,
        vendorCatalogNum: p.vendorCatalogNum,
        catalogNum: p.catalogNum,
        unitType: p.unitType,
        quantity: p.quantity,
        description: p.description,
        desiredPackaging: p.desiredPackaging,
        poNumber: formValue.number,
        costPerUnit: p.unitPrice,
      }))
    };
    this.router.navigate(['/orders/new'], { state: { prefill } });
  }
}
